/**
 * Utility functions for checking if field inputs are complete
 * before triggering AI validation calls.
 */

export const isEmailComplete = (email: string): boolean => {
  if (!email) return false;
  // Basic check: must contain '@' and a domain extension
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export interface PhoneValue {
  country?: string;
  telephoneCode?: string;
  number: string;
}

/**
 * Phone validation trigger rules:
 * - For shipper.phoneNumber and consignee.phoneNumber: ALL 3 are required (number + country + telephoneCode).
 * - For all other phone fields: number is required AND (country OR telephoneCode) is available.
 * - Legacy string support (length >= 7).
 */
export const isPhoneComplete = (phone: PhoneValue | string | null | undefined, fieldId?: string): boolean => {
  if (!phone) return false;
  if (typeof phone === 'string') {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 7;
  }
  const numberStr = phone.number !== undefined && phone.number !== null ? String(phone.number).trim() : '';
  if (!numberStr) return false;

  const hasCountry = !!(phone.country && String(phone.country).trim());
  const hasTelephoneCode = !!(phone.telephoneCode && String(phone.telephoneCode).trim());

  const isShipmentParty =
    fieldId && (fieldId.toLowerCase().includes('shipper') || fieldId.toLowerCase().includes('consignee'));

  if (isShipmentParty) {
    // Shipper / Consignee requires all three values
    return hasCountry && hasTelephoneCode;
  }

  // Other phone fields require number AND (country OR telephoneCode)
  return hasCountry || hasTelephoneCode;
};

export const isGstComplete = (gst: string): boolean => {
  if (!gst) return false;
  // Indian GST is usually 15 characters
  const cleanGst = gst.trim().replace(/[\s-]/g, '');
  return cleanGst.length === 15;
};

export const isPanComplete = (pan: string): boolean => {
  if (!pan) return false;
  // Indian PAN is usually 10 characters
  const cleanPan = pan.trim().replace(/[\s-]/g, '');
  return cleanPan.length === 10;
};

export interface AddressValue {
  country?: string;
  pincode?: string;
  [key: string]: any;
}

export const isAddressComplete = (address: AddressValue | null | undefined): boolean => {
  if (!address) return false;
  const { country, pincode } = address;
  return !!(country && country.trim() && pincode && pincode.trim());
};

export interface ShipmentAddressValue {
  pincode?: string;
  city?: string;
  state?: string;
  country?: string;
  [key: string]: any;
}

/**
 * Shipment address validation should trigger only when ALL of these 4 values are available:
 * pincode, city, state, country.
 */
export const isShipmentAddressComplete = (address: ShipmentAddressValue | null | undefined): boolean => {
  if (!address) return false;
  const { pincode, city, state, country } = address;
  return !!(
    pincode &&
    String(pincode).trim() &&
    city &&
    String(city).trim() &&
    state &&
    String(state).trim() &&
    country &&
    String(country).trim()
  );
};

export interface DimensionValue {
  length?: string | number;
  width?: string | number;
  height?: string | number;
  weight?: string | number;
}

export const isDimensionComplete = (dimension: DimensionValue | null | undefined): boolean => {
  if (!dimension) return false;
  const { length, width, height, weight } = dimension;

  const parseVal = (v: any) => {
    if (v === undefined || v === null || v === '') return 0;
    const num = Number(v);
    return isNaN(num) ? 0 : num;
  };

  const l = parseVal(length);
  const w = parseVal(width);
  const ht = parseVal(height);
  const wt = parseVal(weight);

  return l > 0 && w > 0 && ht > 0 && wt > 0;
};

export interface ShipmentPackageProduct {
  description: string;
  quantity: number | string;
  unitValue: number | string;
  HSNName?: string;
  HSNCode?: string;
}

export interface ShipmentPackageBox {
  length: number | string;
  width: number | string;
  height: number | string;
  weight: number | string;
  products: ShipmentPackageProduct[];
}

export interface ShipmentPackageValue {
  purposeOfShipment?: string;
  insurance: boolean;
  unitOfMeasurement: string;
  currency: string;
  box: ShipmentPackageBox[];
}

/**
 * Shipment package is complete when all required fields and boxes with products are available.
 * Note: insurance boolean is preserved (typeof boolean === 'boolean').
 */
export const isShipmentPackageComplete = (pkg: ShipmentPackageValue | null | undefined): boolean => {
  if (!pkg) return false;
  if (typeof pkg.insurance !== 'boolean') return false;
  if (!pkg.unitOfMeasurement || !String(pkg.unitOfMeasurement).trim()) return false;
  if (!pkg.currency || !String(pkg.currency).trim()) return false;
  if (!Array.isArray(pkg.box) || pkg.box.length === 0) return false;

  for (const b of pkg.box) {
    if (!b) return false;
    const l = Number(b.length);
    const w = Number(b.width);
    const h = Number(b.height);
    const wt = Number(b.weight);
    if (isNaN(l) || l <= 0 || isNaN(w) || w <= 0 || isNaN(h) || h <= 0 || isNaN(wt) || wt <= 0) {
      return false;
    }
    if (!Array.isArray(b.products) || b.products.length === 0) return false;
    for (const p of b.products) {
      if (!p) return false;
      if (!p.description || !String(p.description).trim()) return false;
      const q = Number(p.quantity);
      const uv = Number(p.unitValue);
      if (isNaN(q) || q <= 0 || isNaN(uv) || uv <= 0) return false;
    }
  }

  return true;
};

/**
 * Checks if a value is complete based on its validation type and fieldId context.
 */
export const isValueComplete = (type: string, value: any, fieldId?: string): boolean => {
  switch (type) {
    case 'email':
      return typeof value === 'string' && isEmailComplete(value);
    case 'phone':
      return isPhoneComplete(value, fieldId);
    case 'gst':
      return typeof value === 'string' && isGstComplete(value);
    case 'pan':
      return typeof value === 'string' && isPanComplete(value);
    case 'shipmentAddress':
      return isShipmentAddressComplete(value);
    case 'shipmentPackage':
      return isShipmentPackageComplete(value);
    case 'address':
      return isAddressComplete(value);
    case 'dimension':
      return value !== undefined && value !== null && value !== '' && !isNaN(Number(value)) && Number(value) > 0;
    default:
      return false;
  }
};
